// src/components/rotations/RotationUI.tsx
// Petits composants UI partagés par les écrans de rotations.
import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import { mergeByNumber, RPlayer } from "../../lib/rotations";

export type TR = (key: string, options?: Record<string, unknown>) => string;

export const IconBtn = ({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 active:scale-95"
  >
    {children}
  </button>
);

export const Card = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
    <div className="flex items-center justify-between gap-2 mb-3">
      <h2 className="text-lg font-bold">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

export const Modal = ({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
    <div
      className="w-full sm:max-w-lg max-h-[90dvh] flex flex-col rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-900 shadow-xl"
      onClick={(e) => e.stopPropagation()}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h2 className="font-bold text-lg">{title}</h2>
        <button onClick={onClose} className="p-1" aria-label="close">
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">{footer}</div>
    </div>
  </div>
);

export const SquadModal = ({
  current,
  roster,
  onCourt,
  onClose,
  onSave,
  tr,
}: {
  current: RPlayer[];
  roster: RPlayer[];
  onCourt: (number | null)[];
  onClose: () => void;
  onSave: (squad: RPlayer[]) => void;
  tr: TR;
}) => {
  const [pool, setPool] = useState<RPlayer[]>(() => mergeByNumber(roster, current));
  const [selected, setSelected] = useState<Set<number>>(() => new Set(current.map((p) => p.number)));
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [num, setNum] = useState("");
  const [error, setError] = useState<string | null>(null);

  const toggle = (n: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });

  const add = () => {
    const n = parseInt(num, 10);
    if (Number.isNaN(n) || (!first.trim() && !last.trim())) {
      setError(tr("squad.addError"));
      return;
    }
    if (pool.some((p) => p.number === n)) {
      setError(tr("squad.duplicate", { n }));
      return;
    }
    setPool((prev) => mergeByNumber(prev, [{ number: n, firstName: first.trim(), lastName: last.trim() }]));
    setSelected((prev) => new Set(prev).add(n));
    setFirst("");
    setLast("");
    setNum("");
    setError(null);
  };

  const courtSet = new Set(onCourt.filter((n): n is number => n != null));

  return (
    <Modal
      title={tr("squad.title")}
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto self-center text-sm text-gray-500">
            {tr("squad.count", { n: selected.size })}
          </span>
          <button onClick={onClose} className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 text-sm">
            {tr("actions.cancel")}
          </button>
          <button
            onClick={() => onSave(pool.filter((p) => selected.has(p.number)))}
            className="px-4 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold"
          >
            {tr("actions.validate")}
          </button>
        </>
      }
    >
      <div className="flex justify-between items-center mb-2 text-sm">
        <span className="text-gray-500">{tr("squad.hint")}</span>
        <button
          onClick={() =>
            setSelected(selected.size === pool.length ? new Set() : new Set(pool.map((p) => p.number)))
          }
          className="text-orange-600 font-semibold"
        >
          {selected.size === pool.length ? tr("squad.none") : tr("squad.all")}
        </button>
      </div>
      {pool.length === 0 && <p className="text-sm italic text-gray-500 mb-3">{tr("squad.emptyRoster")}</p>}
      <ul className="space-y-1 mb-4">
        {pool.map((p) => (
          <li key={p.number}>
            <label className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
              <input
                type="checkbox"
                className="h-5 w-5 accent-orange-600"
                checked={selected.has(p.number)}
                onChange={() => toggle(p.number)}
              />
              <span className="w-10 font-mono font-bold">#{p.number}</span>
              <span className="flex-1 truncate">
                {[p.firstName, p.lastName].filter(Boolean).join(" ")}
                {p.position ? <span className="text-gray-500 text-xs"> · {p.position}</span> : null}
              </span>
              {courtSet.has(p.number) && (
                <span className="text-[10px] font-semibold uppercase text-orange-600">{tr("squad.onCourt")}</span>
              )}
            </label>
          </li>
        ))}
      </ul>

      <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-3">
        <p className="text-sm font-semibold mb-2">{tr("squad.addTitle")}</p>
        <div className="grid grid-cols-[4.5rem_1fr_1fr] gap-2">
          <input
            value={num}
            onChange={(e) => setNum(e.target.value.replace(/\D/g, ""))}
            placeholder="#"
            inputMode="numeric"
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-2 text-sm"
          />
          <input
            value={first}
            onChange={(e) => setFirst(e.target.value)}
            placeholder={tr("squad.firstName")}
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-2 text-sm min-w-0"
          />
          <input
            value={last}
            onChange={(e) => setLast(e.target.value)}
            placeholder={tr("squad.lastName")}
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-2 py-2 text-sm min-w-0"
          />
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        <button onClick={add} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-orange-600">
          <Plus size={16} /> {tr("squad.add")}
        </button>
      </div>
    </Modal>
  );
};

